/* Shop by Vehicle - shared selector.
 *
 * Host element opts in with [data-vehicle-widget] and supplies:
 *   data-vehicles-url   URL of assets/vehicles.json
 *   data-redirect-base  collection to land on (default /collections/all)
 *
 * vehicles.json shape: { gens: [{ mk, md, y0, y1, s, t[] }] }
 * `s` is the fitment tag products carry, so a selection resolves to a native
 * Shopify tag-filtered collection URL: /collections/all/<s>
 *
 * Selects are matched by [data-field="..."] or [data-step="..."] so the
 * hero and the standalone vehicle pages can share one implementation.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'cs:vehicle';
  var FIELDS = ['year', 'make', 'model', 'trim'];
  var cache = null;

  function loadVehicles(url) {
    if (cache) return cache;
    cache = fetch(url, { cache: 'force-cache' })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.json();
      })
      .then(function (data) {
        var gens = data && Array.isArray(data.gens) ? data.gens : [];
        if (!gens.length) throw new Error('vehicles.json has no generations');
        return gens;
      });
    return cache;
  }

  function Widget(host) {
    this.host = host;
    this.gens = null;
    this.redirectBase = host.dataset.redirectBase || '/collections/all';

    this.selects = {};
    var self = this;
    FIELDS.forEach(function (f) {
      self.selects[f] = host.querySelector('[data-field="' + f + '"], [data-step="' + f + '"]');
    });

    this.submit = host.querySelector('[data-vehicle-submit]') ||
                  host.querySelector('button[type="submit"]') ||
                  host.querySelector('button');
    this.form = host.matches('form') ? host : host.querySelector('form');
  }

  Widget.prototype.init = function () {
    if (!this.selects.year || !this.submit) return;
    var self = this;
    loadVehicles(this.host.dataset.vehiclesUrl)
      .then(function (gens) {
        self.gens = gens;
        self.populateYears();
        self.bind();
        self.restore();
      })
      .catch(function (err) {
        console.warn('[vehicle-selector] unavailable:', err);
        self.disableAll();
      });
  };

  Widget.prototype.disableAll = function () {
    var self = this;
    FIELDS.forEach(function (f) { if (self.selects[f]) self.selects[f].disabled = true; });
    if (this.submit) this.submit.disabled = true;
  };

  /* --- data ---------------------------------------------------------- */

  Widget.prototype.matching = function (year, make, model) {
    var y = year ? Number(year) : null;
    return this.gens.filter(function (g) {
      return (y === null || (y >= g.y0 && y <= g.y1)) &&
             (!make || g.mk === make) &&
             (!model || g.md === model);
    });
  };

  Widget.prototype.currentGen = function () {
    var year = this.val('year'), make = this.val('make'), model = this.val('model');
    if (!year || !make || !model) return null;
    return this.matching(year, make, model)[0] || null;
  };

  Widget.prototype.val = function (f) {
    return this.selects[f] ? this.selects[f].value : '';
  };

  function uniq(list) {
    return Array.from(new Set(list)).sort();
  }

  /* --- rendering ------------------------------------------------------ */

  Widget.prototype.populateYears = function () {
    var set = new Set();
    this.gens.forEach(function (g) {
      for (var y = g.y1; y >= g.y0; y--) set.add(y);
    });
    var years = Array.from(set).sort(function (a, b) { return b - a; }).map(String);
    this.fill('year', years, 'Year');
    this.selects.year.disabled = false;
  };

  Widget.prototype.fill = function (field, values, placeholder) {
    var select = this.selects[field];
    if (!select) return;
    var current = select.value;
    select.innerHTML = '';
    var opt = document.createElement('option');
    opt.value = '';
    opt.textContent = placeholder;
    opt.disabled = true;
    opt.selected = true;
    select.appendChild(opt);
    values.forEach(function (v) {
      var o = document.createElement('option');
      o.value = v;
      o.textContent = v;
      select.appendChild(o);
    });
    if (values.indexOf(current) !== -1) select.value = current;
    this.mark(select);
  };

  Widget.prototype.mark = function (select) {
    if (select) select.classList.toggle('is-placeholder', !select.value);
  };

  Widget.prototype.reset = function (fields) {
    var self = this;
    fields.forEach(function (f) {
      var s = self.selects[f];
      if (!s) return;
      var label = f.charAt(0).toUpperCase() + f.slice(1);
      s.innerHTML = '<option value="" disabled selected>' + label + '</option>';
      s.disabled = true;
      s.value = '';
      self.mark(s);
    });
  };

  /* --- events --------------------------------------------------------- */

  Widget.prototype.bind = function () {
    var self = this;
    FIELDS.forEach(function (f) {
      var s = self.selects[f];
      if (s) s.addEventListener('change', function () { self.mark(s); });
    });
    this.selects.year.addEventListener('change', function () { self.onYear(); });
    if (this.selects.make) this.selects.make.addEventListener('change', function () { self.onMake(); });
    if (this.selects.model) this.selects.model.addEventListener('change', function () { self.onModel(); });
    if (this.selects.trim) this.selects.trim.addEventListener('change', function () { self.updateSubmit(); });

    if (this.form) {
      this.form.addEventListener('submit', function (e) { self.go(e); });
    } else {
      this.submit.addEventListener('click', function (e) { self.go(e); });
    }
  };

  Widget.prototype.onYear = function () {
    this.reset(['make', 'model', 'trim']);
    var makes = uniq(this.matching(this.val('year')).map(function (g) { return g.mk; }));
    this.fill('make', makes, 'Make');
    if (this.selects.make) this.selects.make.disabled = makes.length === 0;
    this.updateSubmit();
  };

  Widget.prototype.onMake = function () {
    this.reset(['model', 'trim']);
    var models = uniq(this.matching(this.val('year'), this.val('make')).map(function (g) { return g.md; }));
    this.fill('model', models, 'Model');
    if (this.selects.model) this.selects.model.disabled = models.length === 0;
    this.updateSubmit();
  };

  Widget.prototype.onModel = function () {
    this.reset(['trim']);
    var gen = this.currentGen();
    var trims = gen && Array.isArray(gen.t) ? gen.t : [];
    if (trims.length) {
      this.fill('trim', trims, 'Trim');
      if (this.selects.trim) this.selects.trim.disabled = false;
    }
    this.updateSubmit();
  };

  Widget.prototype.updateSubmit = function () {
    this.submit.disabled = !this.currentGen();
  };

  Widget.prototype.restore = function () {
    try {
      var saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
      if (!saved || !saved.year || !saved.make || !saved.model) return;
      this.selects.year.value = saved.year;
      this.onYear();
      if (this.selects.make) this.selects.make.value = saved.make;
      this.onMake();
      if (this.selects.model) this.selects.model.value = saved.model;
      this.onModel();
      if (saved.trim && this.selects.trim) this.selects.trim.value = saved.trim;
      var self = this;
      FIELDS.forEach(function (f) { self.mark(self.selects[f]); });
      this.updateSubmit();
    } catch (e) {}
  };

  Widget.prototype.go = function (e) {
    if (e) e.preventDefault();
    var gen = this.currentGen();
    if (!gen) return;

    var vehicle = {
      year: this.val('year'),
      make: this.val('make'),
      model: this.val('model'),
      trim: this.val('trim') || '',
      slug: gen.s,
    };
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(vehicle)); } catch (err) {}
    document.dispatchEvent(new CustomEvent('vehicle:selected', { detail: vehicle }));

    window.location.href = this.redirectBase.replace(/\/$/, '') + '/' + encodeURIComponent(gen.s);
  };

  /* --- boot ------------------------------------------------------------ */

  function boot(root) {
    (root || document).querySelectorAll('[data-vehicle-widget]').forEach(function (host) {
      if (host.__vehicleWidget) return;
      host.__vehicleWidget = true;
      new Widget(host).init();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(); });
  } else {
    boot();
  }
  // Theme editor re-renders sections without a full page load.
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
})();
